import {
  getIOSPushStatus,
  getPermissionStatus,
  isPushSupported,
  requestNotificationPermission,
  subscribeToPush
} from '@/utils/pushNotifications'

/**
 * Shared "Enable Notifications" gate (P2).
 *
 * Single decision point for what happens when the user asks to enable push
 * notifications — reused by the notification prompt AND the profile checkbox
 * (no duplicated enable-flow).
 *
 * | Device state                        | Action                                              |
 * |-------------------------------------|-----------------------------------------------------|
 * | iOS browser tab (not installed PWA) | coaching "Add to Home Screen" (no permission steps) |
 * | Unsupported (no PushManager / iOS < 16.4) | unsupported notice                          |
 * | Permission `denied`                 | blocked notice, no helper                           |
 * | Permission `granted`                | silent subscribe + success (no helper)              |
 * | Permission `default`                | show awaiting helper, then browser prompt           |
 *
 * IMPORTANT (Safari): in the `default` branch `requestNotificationPermission()`
 * must be called synchronously within the user gesture that triggered the gate.
 * `onAwaitPermission()` must therefore stay synchronous (only set UI state) —
 * awaiting it would lose the gesture context.
 *
 * @param {object} api - API client instance
 * @param {object} store - Vuex store instance
 * @param {object} handlers - { onIOSCoach, onUnsupported, onAwaitPermission, onSubscribed, onDenied, onFailed }
 */
export async function enableNotifications(api, store, handlers = {}) {
  const iosStatus = getIOSPushStatus()

  // iOS Safari tab — push requires "Add to Home Screen"; never request permission
  if (iosStatus === 'not-standalone') {
    return handlers.onIOSCoach?.()
  }

  // iOS standalone < 16.4 or browser without push support
  if (iosStatus === 'too-old' || !isPushSupported()) {
    return handlers.onUnsupported?.()
  }

  const permission = getPermissionStatus()

  if (permission === 'denied') {
    return handlers.onDenied?.()
  }

  if (permission === 'granted') {
    const subscription = await subscribeToPush(api)
    return subscription ? handlers.onSubscribed?.() : handlers.onFailed?.()
  }

  // permission === 'default' — the only case where the awaiting helper is visible
  handlers.onAwaitPermission?.()
  const result = await requestNotificationPermission()

  if (result === 'granted') {
    const subscription = await subscribeToPush(api)
    return subscription ? handlers.onSubscribed?.() : handlers.onFailed?.()
  }

  if (result === 'denied') {
    return handlers.onDenied?.()
  }

  // Browser prompt dismissed without a decision — just close
  return handlers.onFailed?.()
}
